export const DOC_SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    blocks: [
      { type: "p", text: "Xevon is an all-in-one Discord bot covering moderation, security, tickets, leveling, music, economy, voice, and more. This documentation walks you through installation, configuration, and every module — using Xevon's real commands." },
      { type: "list", items: ["Invite Xevon with the official invite link.", "Grant the requested permissions.", "Run /help in your server to browse commands interactively, or use the Commands page here."] },
    ],
  },
  {
    id: "installation",
    title: "Installation",
    blocks: [
      { type: "p", text: "Click \"Add Xevon\" on this website or use the official invite link. Discord's authorization screen will ask you to select a server and approve the requested permissions." },
      { type: "p", text: "Once authorized, Xevon joins your server immediately and is ready to use — no additional setup is required for core features." },
    ],
  },
  {
    id: "permissions",
    title: "Permissions",
    blocks: [
      { type: "p", text: "The invite requests Administrator permission so every module works out of the box. Individual commands still enforce Discord's permission system — a member without moderation permissions cannot ban, mute, or purge, even if they can see the commands." },
      { type: "p", text: "For moderation actions to work, make sure Xevon's highest role sits above the roles of the members it needs to manage in your server's role list." },
    ],
  },
  {
    id: "commands",
    title: "Commands",
    blocks: [
      { type: "p", text: "Most Xevon commands are hybrid — they work both as Discord slash commands (type / in any channel) and as prefix commands. You can change the server prefix with prefix set <prefix> and restore it with prefix reset." },
      { type: "list", items: ["/help — open the interactive help menu", "prefix set <prefix> — set a custom server prefix", "prefix reset — restore the default prefix"] },
    ],
  },
  {
    id: "moderation",
    title: "Moderation",
    blocks: [
      { type: "p", text: "The full staff toolkit — from quick actions to bulk cleanup:" },
      { type: "list", items: [
        "ban <user> [reason] — ban a member",
        "tempban <member> <duration> [reason] — ban for a limited time",
        "softban <member> [reason] — ban and unban to delete messages",
        "hackban <target> [reason] — ban a user by ID who isn't in the server",
        "kick <member> [reason] — kick a member",
        "mute / unmute <member> — timeout or remove a timeout",
        "warn <member> [reason] — issue a warning",
        "warnings <member> — view warning history",
        "clearwarn <member> [amount] — clear warnings",
        "purge [args] — bulk-delete messages, with variants: purge humans, purge user, purgebots, purgelinks, purgeattachments",
        "nick <member> [nickname] — change or clear a nickname",
        "role <member> <role> — add or remove a role",
      ] },
      { type: "p", text: "Log every action to a channel with /log set <category> <channel> — see the Logging section." },
    ],
  },
  {
    id: "security",
    title: "Security",
    blocks: [
      { type: "p", text: "Anti-nuke protects your server from destructive mass actions:" },
      { type: "list", items: [
        "/antinuke enable — turn on protection",
        "/antinuke modules — view detection modules",
        "/antinuke punishment <punishment> — set the response when triggered",
        "/antinuke whitelist add <member> — exempt a trusted member",
        "/antinuke settings — view the current configuration",
      ] },
      { type: "p", text: "Automod keeps chats clean automatically:" },
      { type: "list", items: [
        "/automod enable — turn on automod",
        "/automod module enable <module> — enable a filter (antilink, antiinvite, antispam, antimention, anticaps, antiwordfilter, antiemoji, antizalgo, antinewline, antiduplicate)",
        "/automod module punishment <module> <delete|warn|mute|kick|ban> — set the punishment",
        "/automod wordlist add <words> — block specific words",
        "/automod linkwhitelist add <domains> — allow specific domains",
        "/automod logs set <channel> — log automod actions",
      ] },
      { type: "p", text: "Vanity protection stops strangers changing your server's vanity URL: vanityprotect on, vanitywl <user> to allow, vanitywllist to view." },
    ],
  },
  {
    id: "tickets",
    title: "Tickets",
    blocks: [
      { type: "p", text: "Create a panel so members open private support channels with one click:" },
      { type: "list", items: [
        "/ticketpanel create — create a ticket panel",
        "/ticketoption add <name> <emoji> <channel_prefix> <category> <support_role> — add a panel option",
        "/ticketoption list — view all options",
        "/ticketlogs — view ticket log configuration",
        "/ticketstats — view ticket statistics",
      ] },
    ],
  },
  {
    id: "leveling",
    title: "Leveling",
    blocks: [
      { type: "p", text: "Members earn XP by chatting. Ranks, rank cards, and level role rewards keep your community active:" },
      { type: "list", items: [
        "/level rank [member] — view a rank card",
        "/level leaderboard — view the XP leaderboard",
        "/level role set <level> <role> — award a role at a level",
        "/level channel set <channel> — set level-up announcements",
        "/level xp multiplier <multiplier> — boost XP server-wide",
        "/level givexp / removexp <member> <amount> — adjust XP manually",
      ] },
    ],
  },
  {
    id: "reaction-roles",
    title: "Reaction Roles",
    blocks: [
      { type: "p", text: "Let members self-assign roles by reacting to a message:" },
      { type: "list", items: [
        "/reactionrole add [message_id] — add a reaction role",
        "/reactionrole list <message_id> — view reaction roles on a message",
        "/reactionrole remove <message_id> <role> — remove one",
        "/reactionrole delete <message_id> — remove all from a message",
      ] },
    ],
  },
  {
    id: "voice",
    title: "Voice",
    blocks: [
      { type: "p", text: "VoiceMaster powers Join to Create: members join a hub channel and get their own temporary voice room:" },
      { type: "list", items: [
        "/voicemaster hub create <trigger_name> <category> — create the hub",
        "/voicemaster panel [channel] — send the control panel",
        "/voicemaster rename <name> — rename your room",
        "/voicemaster limit <limit> — set a user limit",
        "/voicemaster claim — claim an abandoned room",
        "/voicemaster transfer <member> — hand over ownership",
      ] },
      { type: "p", text: "Staff can manage any voice channel with the vc commands: vc lock, vc unlock, vc hide, vc limit, vc rename, vc move, vc disconnect, vc mute, and more." },
    ],
  },
  {
    id: "premium",
    title: "Premium",
    blocks: [
      { type: "p", text: "Premium unlocks enhanced limits and priority features. Manage it with these commands:" },
      { type: "list", items: [
        "/premium activate — activate Premium on your server",
        "/premium redeem <license_key> — redeem a key",
        "/premium status — view this server's Premium status",
        "/premium info — view plan details",
      ] },
    ],
  },
  {
    id: "faq",
    title: "FAQ",
    blocks: [
      { type: "p", text: "Common questions about Xevon — what it is, how to add it, permissions, Premium, and data deletion — are answered on the FAQ page." },
    ],
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    blocks: [
      { type: "list", items: [
        "Commands not appearing? Re-invite the bot with the official link to refresh scopes, and check channel permissions.",
        "Bot not responding? Check the Status page for a live view of Bot, API, Database, and Redis health.",
        "Moderation actions failing? Make sure Xevon's highest role is above the target member's highest role.",
        "Dashboard changes not saving? Make sure you're signed in and still have Administrator or Manage Server permission.",
        "Still stuck? Join the support server and open a ticket.",
      ] },
    ],
  },
];
